import { useCallback, useRef } from 'react';
import { WILDCARD } from '@ardoq/common-helpers';
import type {
  BlocksEditedEvent,
  BlocksGraph,
  BlocksNode,
} from 'tabview/visualizations/aq-blocks/types';

/**
 * Whether a group is drawn closed, from the persisted `collapsedGroupIds` setting. The wildcard
 * stands for every group, which is what a collapse-all writes, and an id stands for one.
 */
export const isGroupCollapsed = (
  groupId: string,
  collapsedGroupIds: readonly string[]
): boolean =>
  collapsedGroupIds.includes(WILDCARD) || collapsedGroupIds.includes(groupId);

/** A stored `collapsedGroupIds`, which arrives unchecked from settings, viewpoints and slides. */
export const parseCollapsedGroupIds = (raw: unknown): string[] =>
  Array.isArray(raw)
    ? raw.filter((id): id is string => typeof id === 'string')
    : [];

const groupsBelow = (node: BlocksNode): BlocksNode[] =>
  (node.children ?? []).flatMap(child =>
    child.children ? [child, ...groupsBelow(child)] : []
  );

// A group is a node with children; leaves have `children: null`. Checking the
// root's direct children is enough: a group at any depth has a group ancestor
// that is itself a direct child of the root.
export const graphHasGroups = (graph: BlocksGraph | null): boolean =>
  graph !== null &&
  (graph.root.children ?? []).some(child => child.children !== null);

/** The ids of the groups drawn closed. */
export const toCollapsedGroupIds = (graph: BlocksGraph): string[] =>
  groupsBelow(graph.root)
    .filter(group => !group.open)
    .map(group => group.id);

/** The saved setting with its on-screen part rebuilt from the graph; ids for groups not on screen are kept. */
export const mergeCollapsedGroupIds = (
  graph: BlocksGraph,
  collapsedGroupIds: readonly string[]
): string[] => {
  const onScreen = new Set(groupsBelow(graph.root).map(group => group.id));
  const kept = collapsedGroupIds.filter(
    id => id !== WILDCARD && !onScreen.has(id)
  );
  return [...kept, ...toCollapsedGroupIds(graph)];
};

/** Whether every group is open or closed as the saved setting says. */
export const matchesSavedCollapse = (
  graph: BlocksGraph,
  collapsedGroupIds: readonly string[]
): boolean =>
  groupsBelow(graph.root).every(
    group => isGroupCollapsed(group.id, collapsedGroupIds) === !group.open
  );

export type GroupsOffSavedCollapse = {
  toCollapse: BlocksNode[];
  toExpand: BlocksNode[];
};

/** The groups drawn open that the saved setting closes, and the other way round. */
export const toGroupsOffSavedCollapse = (
  graph: BlocksGraph,
  collapsedGroupIds: readonly string[]
): GroupsOffSavedCollapse => {
  const groups = groupsBelow(graph.root);
  return {
    toCollapse: groups.filter(
      group => group.open && isGroupCollapsed(group.id, collapsedGroupIds)
    ),
    toExpand: groups.filter(
      group => !group.open && !isGroupCollapsed(group.id, collapsedGroupIds)
    ),
  };
};

export type GroupCollapseSaving = {
  /** Saves the groups a reader opened or closed on the canvas. */
  onEdited: (event: BlocksEditedEvent) => void;
  /**
   * Runs `apply` (an expand, collapse or collapse-all on the element) for a collapse the setting
   * has just recorded, without saving the edit it causes: the element reports it synchronously,
   * before the new setting reaches this view.
   */
  applySavedCollapse: (
    collapsedGroupIds: readonly string[],
    apply: VoidFunction
  ) => void;
};

/**
 * Keeps the setting the one source of which groups are open. Edits that leave every group as
 * saved (a drag, or a collapse the view applied from the setting) write nothing.
 */
export const useSaveGroupCollapse = (
  onCollapsedGroupsChange: (collapsedGroupIds: string[]) => void,
  collapsedGroupIds: readonly string[]
): GroupCollapseSaving => {
  const savedRef = useRef(collapsedGroupIds);
  savedRef.current = collapsedGroupIds;
  const applyingRef = useRef(false);
  const onEdited = useCallback(
    ({ detail: { graph } }: BlocksEditedEvent) => {
      if (
        applyingRef.current ||
        !graph ||
        matchesSavedCollapse(graph, savedRef.current)
      ) {
        return;
      }
      onCollapsedGroupsChange(mergeCollapsedGroupIds(graph, savedRef.current));
    },
    [onCollapsedGroupsChange]
  );
  const applySavedCollapse = useCallback(
    (saved: readonly string[], apply: VoidFunction) => {
      savedRef.current = saved;
      applyingRef.current = true;
      try {
        apply();
      } finally {
        applyingRef.current = false;
      }
    },
    []
  );
  return { onEdited, applySavedCollapse };
};
